import { DANCES } from "@/constants/dances";
import { Pause, Play } from "lucide-react";

/** Propriétés d'animation en longhand (évite le mélange shorthand/longhand déconseillé par React). */
const animProps = (name, dur, delay = "0s") => ({
  animationName: name,
  animationDuration: `${dur}s`,
  animationTimingFunction: "ease-in-out",
  animationIterationCount: "infinite",
  animationDelay: delay,
});

const BAG = "#8A5A2E"; // sacoche marron
const BAG_DARK = "#4E2F16";
const BAG_LIGHT = "#A5713C"; // variante plus claire pour distinguer la partenaire
const WOOD = "#D9A45B"; // bois clair des bourdons / chalumeau
const WOOD_LIGHT = "#E8C07A"; // pavillons
const WOOD_DARK = "#3A2410";

/**
 * Cornemuse-personnage fidèle au dessin de référence : sacoche poire marron,
 * deux bourdons à pavillon, sufflet, chalumeau pointé vers la droite (elle regarde
 * donc vers la droite — la partenaire est affichée en miroir pour les danses en vis-à-vis),
 * œil de profil, deux petites pattes sombres qui dansent, notes de musique pendant le jeu.
 * Le corps reste vertical : seules de légères inclinaisons rythmiques sont appliquées.
 */
function Piper({ light = false, playing, bodyName, bodyDur, bodyDelay, feetName, feetDur, feetSync, className = "", style }) {
  const feet = playing ? animProps(feetName, feetDur) : undefined;
  /* Les jeux de pieds « symétriques » ont une version miroir pour le pied droit */
  const feetNameR = feetName === "pf-together" ? "pf-together-r" : feetName === "pf-bend" ? "pf-bend-r" : feetName;
  const feetR = playing ? animProps(feetNameR, feetDur, feetSync ? "0s" : `${-feetDur / 2}s`) : undefined;
  const bag = light ? BAG_LIGHT : BAG;
  return (
    <svg viewBox="0 0 76 90" className={`piper-body ${className}`} style={{ ...style, ...(playing && bodyName ? animProps(bodyName, bodyDur, bodyDelay) : {}) }} aria-hidden="true">
      {/* bourdon unique : long et fin, avec ses bagues claires (façon cabrette) */}
      <line x1="38" y1="24" x2="64" y2="2" stroke={WOOD} strokeWidth="3.4" strokeLinecap="round" />
      <circle cx="47" cy="16.5" r="2.4" fill={WOOD_LIGHT} stroke={BAG_DARK} strokeWidth="1" />
      <circle cx="55" cy="9.5" r="2.4" fill={WOOD_LIGHT} stroke={BAG_DARK} strokeWidth="1" />
      <circle cx="63" cy="3" r="2.8" fill={WOOD_LIGHT} stroke={BAG_DARK} strokeWidth="1.1" />
      {/* sufflet avec anche noire */}
      <line x1="14" y1="26" x2="6" y2="12" stroke={WOOD} strokeWidth="3.6" strokeLinecap="round" />
      <path d="M5 12 L2 5 L7 8 Z" fill="#1E1B18" />
      {/* sacoche (poire) */}
      <path
        d="M33 22 C 47 22 54 35 53 50 C 52 66 45 75 34 75 C 21 75 13 62 14 46 C 15 32 21 22 33 22 Z"
        fill={bag}
        stroke={BAG_DARK}
        strokeWidth="2"
      />
      {/* reflet du ventre */}
      <ellipse cx="26" cy="42" rx="7" ry="13" fill="#FFFFFF" opacity="0.14" transform="rotate(-12 26 42)" />
      {/* double hautbois : deux tubes parallèles bagués, pointés vers la droite */}
      <line x1="44" y1="42" x2="67" y2="50" stroke={WOOD} strokeWidth="4.6" strokeLinecap="round" />
      <line x1="43" y1="49" x2="65" y2="58" stroke={WOOD} strokeWidth="4.6" strokeLinecap="round" />
      <circle cx="53" cy="45" r="2.2" fill={WOOD_LIGHT} stroke={BAG_DARK} strokeWidth="0.9" />
      <circle cx="51.5" cy="52.5" r="2.2" fill={WOOD_LIGHT} stroke={BAG_DARK} strokeWidth="0.9" />
      <ellipse cx="68" cy="50.5" rx="2.6" ry="3.1" fill={WOOD_DARK} stroke={WOOD_LIGHT} strokeWidth="1.4" />
      <ellipse cx="66" cy="58.5" rx="2.6" ry="3.1" fill={WOOD_DARK} stroke={WOOD_LIGHT} strokeWidth="1.4" />
      {/* œil de profil avec reflet */}
      <circle cx="31" cy="36" r="3.6" fill="#1E1B18" />
      <circle cx="29.8" cy="34.7" r="1.2" fill="#FFFFFF" />
      {/* pattes qui dansent */}
      <path className="piper-foot" d="M24 75 h7.5 l2 5.5 l-4 -1.8 l-3.5 3 l-1.5 -3.2 z" fill={WOOD_DARK} style={feet} />
      <path className="piper-foot" d="M39 75 h7.5 l2 5.5 l-4 -1.8 l-3.5 3 l-1.5 -3.2 z" fill={WOOD_DARK} style={feetR} />
      {/* notes de musique pendant la lecture */}
      {playing && (
        <g className="piper-note" style={animProps("pf-note", feetDur * 2)}>
          <circle cx="62" cy="30" r="2.6" fill="#1E1B18" />
          <rect x="63.8" y="18" width="1.8" height="13" rx="0.9" fill="#1E1B18" />
          <path d="M63.8 18 q6 1.5 5 7" stroke="#1E1B18" strokeWidth="1.8" fill="none" strokeLinecap="round" />
        </g>
      )}
      {playing && (
        <g className="piper-note" style={animProps("pf-note", feetDur * 2, `${-feetDur}s`)}>
          <circle cx="71" cy="38" r="2.2" fill="#1E1B18" />
          <rect x="72.5" y="28" width="1.6" height="11" rx="0.8" fill="#1E1B18" />
          <path d="M72.5 28 q5 1.2 4.2 6" stroke="#1E1B18" strokeWidth="1.6" fill="none" strokeLinecap="round" />
        </g>
      )}
    </svg>
  );
}

/** Petite scène de bal cliquable : les cornemuses exécutent la danse du morceau pendant la lecture. */
export default function DanceStage({ dance, playing, onToggle, title, testId }) {
  const d = DANCES[dance] || DANCES[""];
  const bodyName = `pb-${d.body}`;
  const partnerDelay = `${-(d.dur * (d.partnerDelay || 0))}s`;
  const feetName = `pf-${d.feet}`;
  const feetDur = d.dur / 2;

  const piperProps = { playing, feetName, feetDur, feetSync: d.feetSync };
  /* Danses « main dans la main » : sans bras, on rapproche simplement les partenaires */
  const coupleGap = d.hands ? "gap-0.5" : "gap-2";

  return (
    <button
      data-testid={testId}
      onClick={onToggle}
      aria-label={playing ? `Pause ${title}` : `Écouter ${title}`}
      className={`relative flex h-20 w-24 shrink-0 items-end justify-center overflow-hidden rounded-xl px-1.5 pb-1 shadow transition-all duration-300 sm:h-24 sm:w-28 ${
        playing ? "bg-[#2A241E] ring-2 ring-amber-200/60" : "bg-[#1E1B18] hover:ring-2 hover:ring-[#B83227]/50"
      }`}
    >
      {/* parquet */}
      <span aria-hidden="true" className="absolute inset-x-2 bottom-1 border-t border-stone-600/60" />

      {d.layout === "orbit" ? (
        /* Ronde : les cornemuses tournent en rond en restant verticales */
        <span aria-hidden="true" className="absolute inset-1" style={playing ? { animation: `pc-orbit ${d.dur}s linear infinite` } : undefined}>
          <span className="absolute left-1/2 top-0 -translate-x-1/2" style={playing ? { animation: `pc-orbit-counter ${d.dur}s linear infinite` } : undefined}>
            <Piper className="h-9 w-7 sm:h-11 sm:w-8" bodyName={bodyName} bodyDur={d.dur / 2} {...piperProps} />
          </span>
          <span className="absolute bottom-1 left-1/2 -translate-x-1/2" style={playing ? { animation: `pc-orbit-counter ${d.dur}s linear infinite` } : undefined}>
            <Piper light className="h-9 w-7 sm:h-11 sm:w-8" bodyName={bodyName} bodyDur={d.dur / 2} {...piperProps} />
          </span>
        </span>
      ) : d.cross ? (
        /* Croisement : elles passent l'une devant l'autre puis regagnent leur place (partenaire en miroir) */
        <span aria-hidden="true" className="mb-0.5 flex items-end gap-2">
          <span className="piper-cross-a" style={playing ? { animation: `pc-cross-a ${d.dur * 2}s ease-in-out infinite` } : undefined}>
            <Piper className="h-11 w-9 sm:h-14 sm:w-11" bodyName={bodyName} bodyDur={d.dur} {...piperProps} />
          </span>
          <span className="piper-cross-b -scale-x-100">
            <span style={playing ? { animation: `pc-cross-b ${d.dur * 2}s ease-in-out infinite` } : undefined} className="inline-block">
              <Piper light className="h-11 w-9 sm:h-14 sm:w-11" bodyName={bodyName} bodyDur={d.dur} bodyDelay={partnerDelay} {...piperProps} />
            </span>
          </span>
        </span>
      ) : (
        /* Couple face à face (partenaire en miroir) ou chaîne côte à côte */
        <span aria-hidden="true" className={`mb-0.5 flex items-end ${d.layout === "line" ? "gap-0.5" : coupleGap}`}>
          <Piper className="h-11 w-9 sm:h-14 sm:w-11" bodyName={bodyName} bodyDur={d.dur} {...piperProps} />
          <span className={d.layout === "line" ? "" : "-scale-x-100"}>
            <Piper light className="h-11 w-9 sm:h-14 sm:w-11" bodyName={bodyName} bodyDur={d.dur} bodyDelay={partnerDelay} {...piperProps} />
          </span>
        </span>
      )}

      {/* indicateur lecture/pause */}
      <span className={`absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full ${playing ? "bg-amber-200 text-[#1E1B18]" : "bg-white/15 text-white/80"}`}>
        {playing ? <Pause className="h-3 w-3" /> : <Play className="ml-0.5 h-3 w-3" />}
      </span>
    </button>
  );
}
